import { useEffect, useState } from 'react';
import {
  InputRuleApiModel,
  Country,
  Customer,
  GetQuoteValidationResponse,
  SmartSendItemApiModel
} from '~/lib/api/smartsend/types';
import { CustomerVatFields } from '../../lib/helpers/SmartSendValidation';
import AlertWithIcon from '../AlertWithIcon';
import { CustomerSelector } from '../customer/CustomerSelector';
import { DropShopSearch } from '../dropshop/DropShopSearch';

import Loading from '../../../../components/loading/Loading';
import { EditType } from './item/EditType';
import SidePanelSection from '../../../../components/sidepanel-section';
import { CustomerAddressMessage } from './CustomerAddressMessage';
import { CustomerSelectionValidationInput } from './CustomerSelectionValidationInput';

import { P2GBox, P2GText, P2GLinkGroup, P2GLink, P2GAlert } from '@parcel2go/core-ui';
import { GetDefaultRules } from '../../lib/helpers/GetDefaultRules';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';

interface props {
  onUpdate?: (item: SmartSendItemApiModel, valid: boolean) => void;
  getValidation: (item: SmartSendItemApiModel) => Promise<GetQuoteValidationResponse>;
  item: SmartSendItemApiModel;
  getCustomer: () => Promise<Customer[]>;
  getCountries: () => Promise<Country[]>;
  changeTab: (action: EditType) => void;
  isReturn: boolean;
}

const MapCustomerToRules = (countries: Country[], customer: Customer, validation: InputRuleApiModel[]) => {
  const country = countries.find(
    (c) => c.CountryIsoCode === customer.CountryIsoCode && c.CountrySubdivision === customer.CountrySubdivision
  );
  validation.forEach((r) => {
    r.value = customer[r.name] || '';
    if (r.name === 'County' && country?.States?.length > 0 && !r.value) {
      r.value = country.States[0].Code;
    }
  });
};

export const CustomerSelection: React.FC<props> = (props) => {
  const [countries, setCountries] = useState<Country[]>([]);
  const [validation, setValidation] = useState<GetQuoteValidationResponse>();
  const [showSender, setShowSender] = useState(props.isReturn);
  const [showRecipient, setShowRecipient] = useState(!props.isReturn);
  const [loading, setLoading] = useState(true);

  const [taxFieldsMissing, setTaxFieldsMissing] = useState(false);
  const [isCustoms, setIsCustoms] = useState(false);

  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const postcodeMatch =
    props.item.Recipient?.Postcode === props.item.Sender?.Postcode &&
    props.item.Recipient?.Town === props.item.Sender?.Town &&
    props.item.Recipient?.CountryIsoCode === props.item.Sender?.CountryIsoCode;

  useEffect(() => {
    async function getData() {
      const countryResponse = await props.getCountries();
      const val = await props.getValidation(props.item);

      const senderRules = val?.SenderValidation || GetDefaultRules(props.item.Sender?.CountryIsoCode);
      MapCustomerToRules(countryResponse, props.item.Sender, senderRules);

      const recipRules = val?.RecipientValidation || GetDefaultRules(props.item.Recipient?.CountryIsoCode);
      MapCustomerToRules(countryResponse, props.item.Recipient, recipRules);

      // Store State
      setCountries(countryResponse);
      setShowSender(props.isReturn || !isValid(senderRules.filter((x) => !CustomerVatFields.includes(x.name))));

      setTaxFieldsMissing(!!recipRules.concat(senderRules).find((x) => CustomerVatFields.includes(x.name) && !x.value));
      setIsCustoms(val?.Tags?.includes('Customs') === true);

      setValidation({
        ...val,
        RecipientValidation: recipRules.filter((x) => !CustomerVatFields.includes(x.name)),
        SenderValidation: senderRules.filter((x) => !CustomerVatFields.includes(x.name))
      });
      setLoading(false);
    }

    getData();
  }, []);

  useEffect(() => {
    if (props.onUpdate && validation) {
      props.onUpdate(copyValidation(), formIsValid());
    }
  }, [validation]);

  const copyValidation = () => {
    const item = { ...props.item };
    validation.RecipientValidation.forEach((r) => {
      item.Recipient[r.name] = r.value;
    });
    validation.SenderValidation.forEach((r) => {
      item.Sender[r.name] = r.value;
    });
    return item;
  };

  const isValid = (rules: InputRuleApiModel[]): boolean => {
    return !rules.map((r) => !r.required || !!r.value).includes(false);
  };

  const formIsValid = () => {
    return isValid(validation.RecipientValidation) && isValid(validation.SenderValidation) && !postcodeMatch;
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      <CustomerAddressMessage item={props.item} />

      {postcodeMatch && (
        <P2GAlert color="error">
          <P2GText>
            {t('POSTCODE_MATCH_ERROR', { defaultValue: 'Your recipient and sender postcodes are the same.' })}
          </P2GText>
        </P2GAlert>
      )}

      <SidePanelSection
        title={
          props.isReturn
            ? t('RETURN_TO_SUBTITLE', { defaultValue: 'Return To:' })
            : t('SEND_TO_SUBTITLE', { defaultValue: 'Send To:' })
        }
      >
        {!props.isReturn && (
          <P2GBox mb={2}>
            <CustomerSelector
              customers={props.getCustomer}
              onSelect={(c) => {
                const v = { ...validation };
                v.RecipientValidation.forEach((r) => {
                  r.value = c[r.name];
                });

                return setValidation(v);
              }}
              text={t('BROWSE_SAVED_ADDRESSES', { defaultValue: 'Browse Saved Addresses' })}
            />
          </P2GBox>
        )}
        {showRecipient ? (
          <CustomerSelectionValidationInput
            validation={validation.RecipientValidation}
            onUpdate={(v) => setValidation({ ...validation, RecipientValidation: v })}
            countries={countries}
          />
        ) : (
          <P2GBox>
            <P2GText variant="h6">{props.item.Recipient.Name}</P2GText>
            <br />
            <P2GText>
              {props.item.Recipient.AddressLine1} {props.item.Recipient.AddressLine2}, {props.item.Recipient.Postcode}
            </P2GText>
            <P2GLinkGroup pt={1}>
              <P2GLink key="edit" onClick={() => setShowRecipient(true)}>
                {t('EDIT_BUTTON', { defaultValue: 'Edit' })}
              </P2GLink>
            </P2GLinkGroup>
          </P2GBox>
        )}

        {!props.isReturn && validation?.Quote?.Service?.DropOffProviderCode && props.item.Recipient.Postcode && (
          <P2GBox mt={2}>
            <DropShopSearch
              type="CTA"
              customer={props.item.Recipient}
              service={props.item.Service}
              provider={validation.Quote.Service.DropOffProviderCode}
            />
          </P2GBox>
        )}
      </SidePanelSection>

      {validation.SenderValidation.length > 0 && (
        <SidePanelSection
          title={
            props.isReturn
              ? t('FROM_ADDRESS_SUBTITLE', { defaultValue: 'From Address:' })
              : t('SEND_FROM_SUBTITLE', { defaultValue: 'Send From:' })
          }
        >
          {showSender ? (
            <CustomerSelectionValidationInput
              validation={validation.SenderValidation}
              onUpdate={(v) => setValidation({ ...validation, SenderValidation: v })}
              countries={countries}
            />
          ) : (
            <P2GBox data-sentry-mask>
              <P2GText fontWeight="bold">{props.item.Sender.Name}</P2GText>
              <br />
              <P2GText>
                {props.item.Sender.AddressLine1} {props.item.Sender.AddressLine2}, {props.item.Sender.Postcode}
              </P2GText>
              <P2GLinkGroup pt={1}>
                <P2GLink key="edit" onClick={() => setShowSender(true)}>
                  {t('EDIT_BUTTON', { defaultValue: 'Edit' })}
                </P2GLink>
              </P2GLinkGroup>
            </P2GBox>
          )}
          {validation?.Quote?.Service?.DropOffProviderCode && props.item.Sender.Postcode && (
            <P2GBox mt={2}>
              <DropShopSearch
                type="CTA"
                customer={props.item.Sender}
                service={props.item.Service}
                provider={validation.Quote.Service.DropOffProviderCode}
              />
            </P2GBox>
          )}
        </SidePanelSection>
      )}

      {taxFieldsMissing && (
        <SidePanelSection title={t('CUSTOMER_TAX_DETAILS_SUBTITLE', { defaultValue: 'Customer Tax Details' })}>
          <AlertWithIcon>
            <P2GText variant="body2">
              {isCustoms ? (
                <Trans
                  i18nKey="CUSTOMS_TAX_DETAILS_INTRO_TEXT"
                  t={t}
                  components={{ a: <P2GLink onClick={() => props.changeTab('CUSTOMS')} /> }}
                  defaults={`You are required to complete the tax details for you and your recipient. <br />
                  To add/amend these, go to the <a>Customs</a> tab.`}
                />
              ) : (
                <Trans
                  i18nKey="TAXT_IDS_TAX_DETAILS_INTRO_TEXT"
                  t={t}
                  components={{ a: <P2GLink onClick={() => props.changeTab('CUSTOMS')} /> }}
                  defaults={`You are required to complete the tax details for you and your recipient. <br />
                  To add/amend these, go to the <a>Tax Ids</a> tab.`}
                />
              )}
            </P2GText>
          </AlertWithIcon>
        </SidePanelSection>
      )}
    </>
  );
};
